
<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import { useJoakimStore } from '@/stores/useJoakimStore'
import IdgjaldaTable from '@/components/idgjald/IdgjaldTableFull.vue'
import IdgjaldInfo from '@/components/idgjald/IdgjaldInfo.vue'
import VidmidunarTable from '@/components/idgjald/VidmidunarTable.vue'

const { t } = useI18n()
const joakim = useJoakimStore()

type IdgjaldItem = {
  idgjaldaManudur: string // "YYYY-MM" or ISO-like string
  greitt: boolean
  deildNr: string
  launaGreidandi: string
  idgjald: number
  motframlag: number
  vextir: number
  samtals: number
  dagsGreitt?: string
}

const Idgjold = ref<{ idgjold: IdgjaldItem[] } | null>(null)
const filteredIdgjold = ref<IdgjaldItem[]>([])
const selectedYear = ref<number | null>(null)

const monthsWithPaymentsArray = ref<Array<{ month: string; hasPayments: boolean }>>([])
const CarouselKey = ref(0)

function forceRender() {
  CarouselKey.value += 1
}

function getLast12MonthsArray(): string[] {
  const currentDate = new Date()
  const l12M: string[] = []
  // shift two months, collect last 12
  for (let i = 2; i < 14; i++) {
    const d = new Date(currentDate)
    d.setDate(1)
    d.setMonth(currentDate.getMonth() - i)
    l12M.push(`${d.getFullYear()}-${d.getMonth() < 9 ? '0' : ''}${d.getMonth() + 1}`)
  }
  return l12M
}

/** Year is derived ONLY from idgjaldaManudur */
function rowYear(row: IdgjaldItem): number {
  return moment(row.idgjaldaManudur).year()
}

/** Only years present in data (from idgjaldaManudur) */
const years = computed<number[]>(() => {
  const ys = new Set<number>()
  for (const row of (Idgjold.value?.idgjold ?? [])) {
    const y = rowYear(row)
    if (!isNaN(y)) ys.add(y)
  }
  return Array.from(ys).sort((a, b) => b - a)
})

/** Filter by selectedYear, exclude negatives */
function filterTable(year?: number | null) {
  const src = Idgjold.value?.idgjold ?? []
  if (!year) {
    filteredIdgjold.value = src.filter(x => x.samtals >= 0)
    return
  }
  filteredIdgjold.value = src.filter(x => rowYear(x) === year && x.samtals >= 0)
}

function windowResized() {}

onMounted(async () => {
  const res = await joakim.getIdgjold()
  Idgjold.value = res
  joakim.getSameign()

  if (Idgjold.value) {
    // build last-12-months payment status from idgjaldaManudur
    const last12 = getLast12MonthsArray()
    monthsWithPaymentsArray.value = last12.map(m => ({ month: m, hasPayments: false }))
    Idgjold.value.idgjold.forEach(item => {
      const mwp = monthsWithPaymentsArray.value.find(entry => entry.month === item.idgjaldaManudur)
      if (mwp) mwp.hasPayments = mwp.hasPayments || !!item.greitt
    })

    // initial: show all
    filterTable(null)
  }

  window.addEventListener('resize', windowResized)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', windowResized)
})

// If years change and current selection disappears, reset to "all"
watch(years, ys => {
  if (selectedYear.value && !ys.includes(selectedYear.value)) {
    selectedYear.value = null
    filterTable(null)
  }
})

const sameignRettindi = computed(() => joakim.sameignRettindi)
const vidmidunar = computed<any[]>(() => {
  const v = sameignRettindi.value as any
  return Array.isArray(v?.vidmidunarIdgjald) ? v.vidmidunarIdgjald : []
})
const hasVidmidunar = computed<boolean>(() => vidmidunar.value.length > 0)
</script>

<template>
  <div>
    <h1 class="m-b-50 subpage-header">
      {{ t('idgjoldundirsida_header1') }}
    </h1>

    <v-row>
      <v-col class="idgjol-info-container m-b-50" md="7" sm="12">
        <IdgjaldInfo :monthsWithPaymentsArray="monthsWithPaymentsArray" />
      </v-col>
    </v-row>

    <v-row>
      <v-col md="9" sm="12">
        <h2 class="subpage-header m-b-50">
          {{ t('idgjaldarsida_manudum') }}
        </h2>
      </v-col>

      <!-- Parent-owned year selector; empty = all -->
      <v-col md="3" sm="12">
        <v-select
          v-model="selectedYear"
          :items="years"
          :label="t('premiums_choose_year')"
          :placeholder="t('premiums_choose_year')"
          density="compact"
          variant="outlined"
          clearable
          hide-details
          @update:modelValue="val => { filterTable(val); forceRender() }"
        />
      </v-col>

      <v-col md="12">
        <IdgjaldaTable
          :key="CarouselKey"
          :idgjold="filteredIdgjold"
        />
      </v-col>

      <v-col v-if="hasVidmidunar" cols="12">
        <h2 class="subpage-header m-b-50">
          {{ t('idgjaldarsida_vidmidunargjald') }}
        </h2>
      </v-col>

      <v-col v-if="hasVidmidunar" cols="12" class="table-container">
        <VidmidunarTable
          class="subpage__table subpage__table-default"
          :vidmidun="vidmidunar"
        />
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
.idgjol-info-container {
  max-width: 64.333333%;
}

@media (max-width: 1500px) {
  .idgjol-info-container {
    min-width: 100%;
    overflow: scroll;
  }
}
</style>
